import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { envelopesApi, type EnvelopeView } from '@/api/envelopes.api';
import {
  type FieldInput,
  type FieldType,
  type FieldView,
  fieldsApi,
} from '@/api/fields.api';
import { cn } from '@/lib/cn';
import '@/lib/pdfWorker';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const FIELD_TYPES: Array<{ type: FieldType; label: string; icon: string; defaultSize: { w: number; h: number } }> = [
  { type: 'signature', label: 'Signature', icon: '✍️', defaultSize: { w: 22, h: 7 } },
  { type: 'initial',   label: 'Initial',   icon: 'Aa',  defaultSize: { w: 8,  h: 7 } },
  { type: 'date',      label: 'Date',      icon: '📅', defaultSize: { w: 18, h: 4 } },
  { type: 'text',      label: 'Text',      icon: '📝', defaultSize: { w: 22, h: 4 } },
  { type: 'checkbox',  label: 'Checkbox',  icon: '☑️', defaultSize: { w: 4,  h: 4 } },
  { type: 'name',      label: 'Name',      icon: '👤', defaultSize: { w: 22, h: 4 } },
  { type: 'email',     label: 'Email',     icon: '📧', defaultSize: { w: 22, h: 4 } },
  { type: 'company',   label: 'Company',   icon: '🏢', defaultSize: { w: 22, h: 4 } },
  { type: 'note',      label: 'Note',      icon: '🗒️', defaultSize: { w: 26, h: 12 } },
];

const RECIPIENT_COLORS = [
  { ring: 'ring-blue-400',    bg: 'bg-blue-500/10',    text: 'text-blue-700',    border: 'border-blue-400',   solid: '#3b82f6' },
  { ring: 'ring-emerald-400', bg: 'bg-emerald-500/10', text: 'text-emerald-700', border: 'border-emerald-400', solid: '#10b981' },
  { ring: 'ring-amber-400',   bg: 'bg-amber-500/10',   text: 'text-amber-700',   border: 'border-amber-400',  solid: '#f59e0b' },
  { ring: 'ring-purple-400',  bg: 'bg-purple-500/10',  text: 'text-purple-700',  border: 'border-purple-400', solid: '#8b5cf6' },
  { ring: 'ring-pink-400',    bg: 'bg-pink-500/10',    text: 'text-pink-700',    border: 'border-pink-400',   solid: '#ec4899' },
];

type DraftField = Omit<FieldInput, never> & {
  /** Local id used while unsaved. Real id assigned after save. */
  localId: string;
};

type Props = {
  envelope: EnvelopeView;
};

export type FieldsStepHandle = {
  /** Persist any unsaved drafts. Returns the number of fields after save. */
  saveIfDirty: () => Promise<number>;
  fieldCount: () => number;
};

export const FieldsStep = forwardRef<FieldsStepHandle, Props>(function FieldsStep({ envelope }, ref) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fieldTypeLabel = (type: FieldType) => t(`fields_step.type_${type}`);

  // Fields are owned locally as draft state; we sync on Save.
  const { data: serverFields = [], isLoading: fieldsLoading } = useQuery({
    queryKey: ['fields', envelope.id],
    queryFn: () => fieldsApi.list(envelope.id),
  });

  const [activeDocId, setActiveDocId] = useState<string | null>(envelope.documents[0]?.id ?? null);
  const [activePage, setActivePage] = useState(1);
  const [activeRecipientId, setActiveRecipientId] = useState<string | null>(
    // Approvers and CC receive no fields — start with the first signer/witness.
    envelope.recipients.find((r) => r.role === 'signer' || r.role === 'witness')?.id ?? null,
  );
  const [drafts, setDrafts] = useState<DraftField[]>([]);
  const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const lastUrlRef = useRef<string | null>(null);
  const [pdfPages, setPdfPages] = useState<number>(envelope.documents.find(d => d.id === activeDocId)?.pagesCount ?? 1);
  const pageWrapperRef = useRef<HTMLDivElement | null>(null);

  // Hydrate drafts from server fields on initial load.
  useEffect(() => {
    setDrafts(
      serverFields.map((f) => ({
        localId: f.id,
        documentId: f.documentId,
        recipientId: f.recipientId,
        type: f.type,
        pageNumber: f.pageNumber,
        posX: f.posX,
        posY: f.posY,
        width: f.width,
        height: f.height,
        isRequired: f.isRequired,
        defaultValue: f.defaultValue ?? undefined,
      })),
    );
  }, [serverFields]);

  // Fetch PDF as blob when active document changes.
  useEffect(() => {
    if (!activeDocId) return;
    let cancelled = false;
    documentsApi.fetchAsBlobUrl(activeDocId).then((url) => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
      lastUrlRef.current = url;
      setPdfUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [activeDocId]);

  useEffect(() => () => {
    if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
  }, []);

  const saveMutation = useMutation({
    mutationFn: () => {
      const inputs: FieldInput[] = drafts.map(({ localId: _localId, ...rest }) => rest);
      return fieldsApi.bulkSave(envelope.id, inputs);
    },
    onSuccess: (saved) => {
      // Re-key drafts to server ids.
      setDrafts(
        saved.map((f) => ({
          localId: f.id,
          documentId: f.documentId,
          recipientId: f.recipientId,
          type: f.type,
          pageNumber: f.pageNumber,
          posX: f.posX,
          posY: f.posY,
          width: f.width,
          height: f.height,
          isRequired: f.isRequired,
          defaultValue: f.defaultValue ?? undefined,
        })),
      );
      qc.invalidateQueries({ queryKey: ['fields', envelope.id] });
      toast.success(`Saved ${saved.length} field${saved.length === 1 ? '' : 's'}`);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  // Moved here from the Recipients step (Step 3 → Step 4): the envelope-level
  // "accept without signature" switch belongs with the field-type setup.
  const signatureOptionalMutation = useMutation({
    mutationFn: (value: boolean) => envelopesApi.update(envelope.id, { signatureOptional: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const recipientColor = useMemo(() => {
    const map = new Map<string, (typeof RECIPIENT_COLORS)[number]>();
    envelope.recipients.forEach((r, i) => map.set(r.id, RECIPIENT_COLORS[i % RECIPIENT_COLORS.length]));
    return map;
  }, [envelope.recipients]);

  // ─── Add field by clicking the page (after a palette item is selected) ──
  const [pendingType, setPendingType] = useState<FieldType | null>(null);

  function handlePageClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!pendingType || !activeDocId || !activeRecipientId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    const meta = FIELD_TYPES.find((f) => f.type === pendingType)!;
    // Center the field on the click location, clamp to bounds.
    const px = clamp(xPct - meta.defaultSize.w / 2, 0, 100 - meta.defaultSize.w);
    const py = clamp(yPct - meta.defaultSize.h / 2, 0, 100 - meta.defaultSize.h);
    const newField: DraftField = {
      localId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      documentId: activeDocId,
      recipientId: activeRecipientId,
      type: pendingType,
      pageNumber: activePage,
      posX: round(px),
      posY: round(py),
      width: meta.defaultSize.w,
      height: meta.defaultSize.h,
      isRequired: true,
    };
    setDrafts((prev) => [...prev, newField]);
    setSelectedLocalId(newField.localId);
    setPendingType(null);
  }

  function moveField(localId: string, dxPct: number, dyPct: number) {
    setDrafts((prev) =>
      prev.map((f) =>
        f.localId === localId
          ? {
              ...f,
              posX: round(clamp(f.posX + dxPct, 0, 100 - f.width)),
              posY: round(clamp(f.posY + dyPct, 0, 100 - f.height)),
            }
          : f,
      ),
    );
  }

  function updateSelected(patch: Partial<DraftField>) {
    if (!selectedLocalId) return;
    setDrafts((prev) =>
      prev.map((f) => (f.localId === selectedLocalId ? { ...f, ...patch } : f)),
    );
  }

  function removeSelected() {
    if (!selectedLocalId) return;
    setDrafts((prev) => prev.filter((f) => f.localId !== selectedLocalId));
    setSelectedLocalId(null);
  }

  const selected = drafts.find((f) => f.localId === selectedLocalId) ?? null;
  const fieldsOnActivePage = drafts.filter(
    (f) => f.documentId === activeDocId && f.pageNumber === activePage,
  );

  const dirty = !sameFields(serverFields, drafts);

  // Every recipient — signer or approver — can be assigned any field type
  // independently (patron: each approver may have a different field type, not
  // all locked to Signature). Fields are stored per recipientId, so a different
  // type placed for one approver does not affect another.
  const availableTypes = FIELD_TYPES;

  // Imperative hook so the wizard can flush unsaved drafts before navigating
  // to Review — otherwise users silently lose their field placements.
  useImperativeHandle(ref, () => ({
    fieldCount: () => drafts.length,
    saveIfDirty: async () => {
      if (!dirty) return drafts.length;
      const saved = await saveMutation.mutateAsync();
      return saved.length;
    },
  }), [drafts, dirty, saveMutation]);

  if (envelope.documents.length === 0 || envelope.recipients.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-3xl mb-2">📐</div>
        <h3 className="text-base font-semibold text-gray-900">{t('wizard.documents_required')}</h3>
        <p className="text-sm text-muted mt-1">{t('wizard.recipients_required')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">{t('fields_step.title')}</h3>
          <p className="text-[13px] text-muted">
            {pendingType ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: t('fields_step.subtitle_drop', {
                    type: fieldTypeLabel(pendingType),
                    name: envelope.recipients.find((r) => r.id === activeRecipientId)?.name ?? '',
                  }),
                }}
              />
            ) : (
              t('fields_step.subtitle_pick')
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-warning">{t('fields_step.unsaved')}</span>}
          <button
            className="btn btn-primary"
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? t('common.saving') : t('fields_step.save_fields')}
          </button>
        </div>
      </div>

      {/* Envelope-level "accept without signature" switch (moved from Step 3). */}
      <label className="card !py-2.5 !px-3 flex items-start gap-2 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={envelope.signatureOptional}
          disabled={signatureOptionalMutation.isPending}
          onChange={(e) => signatureOptionalMutation.mutate(e.target.checked)}
          className="mt-0.5 accent-primary"
        />
        <span className="text-[13px]">
          <span className="font-medium text-gray-900">{t('recipients_step.signature_optional_label')}</span>
          <span className="block text-xs text-muted mt-0.5">{t('recipients_step.signature_optional_hint')}</span>
        </span>
      </label>

      <div className="grid grid-cols-[180px_1fr_240px] gap-4">
        {/* ─── Left palette ─── */}
        <aside className="card !p-2 self-start sticky top-20">
          <div className="px-2 py-1.5">
            <div className="text-[10px] uppercase font-semibold text-muted tracking-wider">
              1. {t('fields_step.recipient')}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              {t('fields_step.recipient_help')}
            </div>
          </div>
          <div className="px-1 mb-3 space-y-1">
            {envelope.recipients.map((r) => {
              const color = recipientColor.get(r.id);
              const active = r.id === activeRecipientId;
              const isCc = r.role === 'cc';
              // Approvers may now place their own (signature-only) field; only
              // CC recipients get no fields.
              const placeable = !isCc;
              const fieldCount = drafts.filter((f) => f.recipientId === r.id).length;
              return (
                <button
                  key={r.id}
                  onClick={() => placeable && setActiveRecipientId(r.id)}
                  disabled={!placeable}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-2 rounded-md text-[12px] text-left transition-all border-2',
                    !placeable && 'opacity-60 cursor-not-allowed border-transparent',
                    active && placeable
                      ? `${color?.bg} ${color?.text} ${color?.border}`
                      : placeable && 'text-gray-700 hover:bg-gray-100 border-transparent',
                  )}
                  title={isCc ? t('envelope.role_cc') : undefined}
                >
                  <span className={cn('w-3 h-3 rounded-full inline-block shrink-0 border border-white/50')}
                        style={{ background: color?.solid }} />
                  <span className="truncate flex-1">{r.name}</span>
                  {placeable && fieldCount > 0 && (
                    <span className="text-[10px] bg-white/60 text-gray-900 rounded-full px-1.5 py-0 font-semibold">
                      {fieldCount}
                    </span>
                  )}
                  {!placeable && (
                    <span className="ml-auto text-[9px] uppercase text-muted font-semibold">
                      {r.role}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-2 py-1.5 border-t border-border mt-1">
            <div className="text-[10px] uppercase font-semibold text-muted tracking-wider">
              2. {t('fields_step.field_types')}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              {t('fields_step.field_types_help')}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1 px-1">
            {availableTypes.map((f) => {
              const label = fieldTypeLabel(f.type);
              return (
                <button
                  key={f.type}
                  onClick={() => setPendingType((p) => (p === f.type ? null : f.type))}
                  className={cn(
                    'flex flex-col items-center gap-1 px-2 py-2 rounded-md border text-[11px] font-medium transition-colors',
                    pendingType === f.type
                      ? 'border-primary bg-primary text-white'
                      : 'border-border text-gray-700 hover:border-primary hover:bg-primary-50',
                  )}
                  title={label}
                >
                  <span className="text-base leading-none">{f.icon}</span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ─── Center: PDF + page picker ─── */}
        <main>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            {/* Document selector */}
            <div className="flex items-center gap-2">
              {envelope.documents.length > 1 && (
                <select
                  className="input !py-1.5 text-[13px]"
                  value={activeDocId ?? ''}
                  onChange={(e) => {
                    setActiveDocId(e.target.value);
                    setActivePage(1);
                    const doc = envelope.documents.find(d => d.id === e.target.value);
                    if (doc) setPdfPages(doc.pagesCount);
                  }}
                >
                  {envelope.documents.map((d) => (
                    <option key={d.id} value={d.id}>{d.originalName}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Page nav */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActivePage((p) => Math.max(1, p - 1))}
                disabled={activePage <= 1}
                className="btn btn-secondary btn-sm"
              >
                ←
              </button>
              <span className="text-[13px] text-muted whitespace-nowrap">
                Page {activePage} / {pdfPages}
              </span>
              <button
                onClick={() => setActivePage((p) => Math.min(pdfPages, p + 1))}
                disabled={activePage >= pdfPages}
                className="btn btn-secondary btn-sm"
              >
                →
              </button>
            </div>
          </div>

          {pdfUrl ? (
            <div
              ref={pageWrapperRef}
              onClick={handlePageClick}
              className={cn(
                'relative bg-white border border-border rounded-md mx-auto inline-block',
                pendingType && 'cursor-crosshair',
              )}
              style={{ minHeight: 400 }}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={(p) => setPdfPages(p.numPages)}
                loading={<div className="p-12 text-muted">{t('fields_step.loading_pdf')}</div>}
                error={<div className="p-12 text-danger">{t('fields_step.failed_to_load_pdf')}</div>}
              >
                <Page
                  pageNumber={activePage}
                  width={620}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>

              {/* Field overlay */}
              {fieldsOnActivePage.map((f) => (
                <FieldMarkerView
                  key={f.localId}
                  field={f}
                  selected={selectedLocalId === f.localId}
                  color={recipientColor.get(f.recipientId)!}
                  recipientName={envelope.recipients.find(r => r.id === f.recipientId)?.name ?? ''}
                  onSelect={() => setSelectedLocalId(f.localId)}
                  onMove={(dx, dy) => moveField(f.localId, dx, dy)}
                />
              ))}
            </div>
          ) : (
            <div className="card py-12 text-center text-muted">{t('fields_step.loading_pdf')}</div>
          )}

          {fieldsLoading && <div className="text-xs text-muted mt-2">{t('common.loading')}</div>}
        </main>

        {/* ─── Right: properties ─── */}
        <aside className="card !p-3 self-start sticky top-20">
          <div className="text-[10px] uppercase font-semibold text-muted tracking-wider mb-2">
            {t('fields_step.selected_field')}
          </div>
          {!selected ? (
            <div className="text-[13px] text-muted">
              {t('fields_step.click_to_edit')}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-[13px] text-gray-900">
                <strong>{fieldTypeLabel(selected.type)}</strong>
                <div className="text-[11px] text-muted">
                  {t('common.page')} {selected.pageNumber} · {selected.posX.toFixed(1)}%, {selected.posY.toFixed(1)}%
                </div>
              </div>

              <div>
                <label className="label">{t('fields_step.recipient')}</label>
                <select
                  className="input"
                  value={selected.recipientId}
                  onChange={(e) => updateSelected({ recipientId: e.target.value })}
                >
                  {envelope.recipients.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">Width %</label>
                  <input
                    className="input"
                    type="number"
                    min={0.5}
                    max={50}
                    step={0.5}
                    value={selected.width}
                    onChange={(e) => updateSelected({ width: clamp(Number(e.target.value), 0.5, 50) })}
                  />
                </div>
                <div>
                  <label className="label">Height %</label>
                  <input
                    className="input"
                    type="number"
                    min={0.5}
                    max={30}
                    step={0.5}
                    value={selected.height}
                    onChange={(e) => updateSelected({ height: clamp(Number(e.target.value), 0.5, 30) })}
                  />
                </div>
              </div>

              {selected.type === 'note' && (
                <div>
                  <label className="label">{t('fields_step.note_text_label', 'Note text')}</label>
                  <textarea
                    className="input min-h-[80px] text-[13px]"
                    value={selected.defaultValue ?? ''}
                    maxLength={2000}
                    placeholder={t('fields_step.note_text_placeholder', 'Write the note that will appear on the document…') as string}
                    onChange={(e) => updateSelected({ defaultValue: e.target.value })}
                  />
                  <p className="text-[11px] text-muted mt-1">
                    {t('fields_step.note_text_hint', 'This text is placed on the PDF. The approver can still edit it while reviewing.')}
                  </p>
                </div>
              )}

              <label className="flex items-center gap-2 text-[13px] text-gray-900 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.isRequired ?? true}
                  onChange={(e) => updateSelected({ isRequired: e.target.checked })}
                  className="accent-primary"
                />
                {t('fields_step.required')}
              </label>

              <button
                className="btn btn-secondary w-full text-danger"
                onClick={removeSelected}
              >
                {t('fields_step.remove')}
              </button>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted leading-relaxed">
            {drafts.length === 0
              ? t('fields_step.no_fields_placed')
              : t('fields_step.fields_placed_count', { count: drafts.length })}
          </div>
        </aside>
      </div>
    </div>
  );
});

// ────────────────────────────────────────────────────────────────

function FieldMarkerView({
  field,
  selected,
  color,
  recipientName,
  onSelect,
  onMove,
}: {
  field: DraftField;
  selected: boolean;
  color: (typeof RECIPIENT_COLORS)[number];
  recipientName: string;
  onSelect: () => void;
  onMove: (dxPct: number, dyPct: number) => void;
}) {
  const meta = FIELD_TYPES.find((f) => f.type === field.type)!;

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    onSelect();
    const parent = e.currentTarget.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    // Track the previous pointer position and report the INCREMENTAL delta on
    // each move. moveField() applies the delta additively (posX + dxPct), so
    // the delta must be measured from the last event, not from the gesture
    // start — otherwise every event re-adds the growing from-start offset and
    // the field accelerates away from the cursor.
    let lastX = e.clientX;
    let lastY = e.clientY;

    function handleMove(ev: MouseEvent) {
      const dxPct = ((ev.clientX - lastX) / rect.width) * 100;
      const dyPct = ((ev.clientY - lastY) / rect.height) * 100;
      lastX = ev.clientX;
      lastY = ev.clientY;
      onMove(dxPct, dyPct);
    }
    function handleUp() {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    }
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={cn(
        'absolute border-2 cursor-move flex items-center justify-center text-[11px] font-medium select-none',
        color.bg,
        color.text,
        color.border,
        selected && `ring-2 ring-offset-1 ${color.ring}`,
      )}
      style={{
        left: `${field.posX}%`,
        top: `${field.posY}%`,
        width: `${field.width}%`,
        height: `${field.height}%`,
        borderRadius: 4,
      }}
      title={`${field.type} · ${recipientName}`}
    >
      {field.type === 'note' && field.defaultValue ? (
        <span className="w-full h-full overflow-hidden whitespace-pre-wrap text-left leading-tight p-1">
          {field.defaultValue}
        </span>
      ) : (
        <span className="truncate px-1">
          {meta.icon} {meta.label}
        </span>
      )}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function round(n: number) {
  return Math.round(n * 100) / 100;
}
function sameFields(server: FieldView[], drafts: DraftField[]) {
  if (server.length !== drafts.length) return false;
  const key = (f: { documentId: string; recipientId: string; type: string; pageNumber: number;
                    posX: number; posY: number; width: number; height: number; isRequired?: boolean;
                    defaultValue?: string | null }) =>
    `${f.documentId}|${f.recipientId}|${f.type}|${f.pageNumber}|${f.posX}|${f.posY}|${f.width}|${f.height}|${f.isRequired ?? true}|${f.defaultValue ?? ''}`;
  const a = new Set(server.map(key));
  return drafts.every((d) => a.has(key(d)));
}
